import { IV3D } from '@/Interfaces/v3d';
import { Box3, Line3, Object3D, Quaternion, Ray, Vector3 } from 'three';
import { LineDotted } from '@/Engine/Primitives/LineDotted';
import { WardrobeObject } from '@/Engine/Wardrobe/WardrobeObject';
import { Door } from '@/Engine/Door';
import { Window } from '@/Engine/Window';
import { rayIntersection, sides } from '@/Interfaces/common';
import { RackModule } from '@/Engine/Wardrobe/RackModule';
import { ShelfSet } from '@/Engine/Wardrobe/ShelfSet';
import { PerforatedPanel } from '@/Engine/Wardrobe/PerforatedPanel';
import { $ } from '@/Engine/state';
import { TextLine } from '@/Engine/Ruler/TextLine';
import { TextMesh } from '@/Engine/Ruler/TextMesh';
import { OBB } from 'three/examples/jsm/math/OBB';
import { transformPositionBySide } from '@/lib/transformPositionBySide';
import { useUIOnlyStore } from '@/Store/storeUI';
import { markCollisions } from '@/Engine/eventHandlers/lib/markCollisions';
import { BoxItem } from '@/Engine/Box';

declare let v3d: IV3D;
type target = RackModule | ShelfSet | PerforatedPanel | Window | Door | BoxItem
export type selectionBoxTarget = target;
export class SelectionBox {
    model: Object3D;
    depth = 1;
    height = 1;
    width = 1;
    target: target | null;
    targetGroup: Array<RackModule> = [];
    side: sides | null = null;
    dockingTarget: any | null;
    dockingShelfTemplate: any | null;
    rulerUp: TextLine;
    rulerDown: TextLine;
    rulerLeft: TextLine;
    rulerRight: TextLine;
    obb = new OBB();
    upIntersection: rayIntersection | null;
    downIntersection: rayIntersection | null;
    leftIntersection: rayIntersection | null;
    rightIntersection: rayIntersection | null;
    public rays = {
        up: new v3d.Ray(),
        down: new v3d.Ray(),
        left: new v3d.Ray(),
        right: new v3d.Ray(),
    };

    set visible(state: boolean) {
        this.model.visible = state;
        this.model.children.forEach((child)=>{
            child.visible = state;
        });
    }

    get visible() {
        return this.model.visible;
    }
    constructor() {
        const startPoint = new v3d.Vector3(1,1,1);
        const depth = 1;
        const height = 1;
        const width = 1;
        this.target = null;
        this.model = new v3d.Group();
        // this.model.visible = false;
        this.model.position.copy(startPoint);
        this.updateGeometry();
        this.rulerUp = new TextLine(new Line3());
        this.rulerDown = new TextLine(new Line3());
        this.rulerLeft = new TextLine(new Line3());
        this.rulerRight = new TextLine(new Line3());
    }

    select(target: target) {
        $.selectionBoxForBox.unselect();
        this.targetGroup = [];
        this.target = target;

        // this.model.position.set(...target.startPoint);
        this.side = this.target.model.userData.side;
        // this.side
        // this.model.position.copy(target.model.position);
        this.update();
        // this.updateRuler();
    }

    selectGroup(targetGroup: Array<target>) {
        this.unselect();
        this.targetGroup = targetGroup;
        this.side = this.targetGroup[0].model.userData.side;
        // const firstModule = this.targetGroup[0].getFirstModule();
        // this.model.position.set(...firstModule.startPoint);
        // Надо найти boundingBox и центр группы
        this.update();

        // this.targetGroup.push();
    }

    updateIntersections() {
        if (!this.target) return;
        const excludeUuids = [];
        if (this.target.parent) {
            excludeUuids.push(this.target.parent.uuid);
        }
        if (this.target.rackModule) {
            excludeUuids.push(this.target.rackModule.uuid);
        }
        if (this.target.railGroup) {
            excludeUuids.push(this.target.railGroup.uuid);
        }
        if (this.target.shelves?.length) {
            excludeUuids.push(...this.target.shelves.map((shelf)=>shelf.uuid));
        }
        let intersectObjects;

        if (this.target.type === 'rackModule') {
            intersectObjects = $.getAllObjectsWithoutShelf();
        } else {
            intersectObjects = $.getAllObjectsWithExclude(excludeUuids);
        }

        this.upIntersection = this.target.getLowerObject(this.target.rays.up, intersectObjects);
        this.downIntersection = this.target.getLowerObject(this.target.rays.down, intersectObjects);
        this.leftIntersection = this.target.getLowerObject(this.target.rays.left, intersectObjects);
        this.rightIntersection = this.target.getLowerObject(this.target.rays.right, intersectObjects);
    }

    getLowerObject(ray: Ray, objects: Array<WardrobeObject>) {
        const out: rayIntersection = {
            distance: null,
            object: null,
            point: null,
        };

        objects.forEach((child)=> {
            const point = new Vector3();
            const intersect = child.obb.intersectRay(ray, point);
            if (!intersect) return null;
            const line =  new v3d.Line3(ray.origin, intersect);
            if (!out.distance || out.distance > line.distance()) {
                out.distance = line.distance();
                out.object = child;
                out.point = intersect;
            }
        });

        return out;
    }

    updateIntersectionsForGroup() {
        if (!this.targetGroup.length) return;
        const excludeUuids = [];
        excludeUuids.push(...this.targetGroup.map((rack)=>rack.uuid));
        // const groupCenter = this.obb.center.clone();
        const intersectObjects = $.getAllObjectsWithoutShelfWithExclude(excludeUuids);
        const quaternion = new Quaternion();
        this.targetGroup[0].model.getWorldQuaternion(quaternion);
        const rayUpPoint = new Vector3();
        const rayDownPoint = new Vector3();
        const rayLeftPoint = new Vector3();
        const rayRightPoint = new Vector3();
        let groupSizes = new Vector3();
        this.obb.getSize(groupSizes);
        const invertQuart = quaternion.clone().invert();
        groupSizes.applyQuaternion(invertQuart);
        groupSizes = new Vector3(
            Math.abs(groupSizes.x),
            Math.abs(groupSizes.y),
            Math.abs(groupSizes.z),
        );
        const groupCenter = this.obb.center.clone()
            .sub(new Vector3(0,0,groupSizes.z/2)
                .applyQuaternion(quaternion));
        const rayOrigin = new Vector3(0,0,0)
            .applyQuaternion(quaternion)
            .add(groupCenter);

        rayUpPoint.copy(rayOrigin);

        rayUpPoint.set(
            rayOrigin.x,
            rayOrigin.y,
            rayOrigin.z,
        );

        rayDownPoint.set(
            rayOrigin.x,
            rayOrigin.y,
            rayOrigin.z,
        );

        rayLeftPoint.set(
            rayOrigin.x,
            rayOrigin.y,
            rayOrigin.z,
        );

        rayRightPoint.set(
            rayOrigin.x,
            rayOrigin.y,
            rayOrigin.z,
        );
        // rayLeftPoint.set(
        //     -this.localSize.x/2,
        //     -this.localSize.y/2 + yShift,
        //     0)
        //     .applyQuaternion(worldQuaternion)
        //     .add(rayOrigin);
        //
        // rayRightPoint.set(
        //     this.localSize.x/2,
        //     -this.localSize.y/2 + yShift,
        //     0)
        //     .applyQuaternion(worldQuaternion)
        //     .add(rayOrigin);

        this.rays.up.set(rayUpPoint, new Vector3(0,1,0));
        this.rays.down.set(rayDownPoint, new Vector3(0,-1,0));
        this.rays.left.set(rayLeftPoint, new Vector3(-1,0,0).applyQuaternion(quaternion));
        this.rays.right.set(rayRightPoint, new Vector3(1,0,0).applyQuaternion(quaternion));

        this.upIntersection = this.getLowerObject(this.rays.up, intersectObjects);
        this.downIntersection = this.getLowerObject(this.rays.down, intersectObjects);
        this.leftIntersection = this.getLowerObject(this.rays.left, intersectObjects);
        this.rightIntersection = this.getLowerObject(this.rays.right, intersectObjects);

    }

    updateRulerForGroup() {
        if (!this.targetGroup.length || !$.isRuler) {
            this.rulerUp.model.removeFromParent();
            this.rulerDown.model.removeFromParent();
            this.rulerLeft.model.removeFromParent();
            this.rulerRight.model.removeFromParent();
            return;
        }
        this.updateIntersectionsForGroup();

        let groupSizes = new Vector3();
        this.obb.getSize(groupSizes);
        const quaternion = new Quaternion();
        this.targetGroup[0].model.getWorldQuaternion(quaternion);

        const invertQuart = quaternion.clone().invert();
        groupSizes.applyQuaternion(invertQuart);
        groupSizes = new Vector3(
            Math.abs(groupSizes.x),
            Math.abs(groupSizes.y),
            Math.abs(groupSizes.z),
        );
        const groupCenter = this.obb.center.clone()
            .sub(new Vector3(0,0,groupSizes.z/2)
                .applyQuaternion(quaternion));
        const leftPoint = groupCenter.clone()
            // .applyQuaternion(invertQuart)
            .sub(new Vector3(groupSizes.x/2,0,0)
                .applyQuaternion(quaternion));
        const rightPoint = groupCenter.clone()
            // .applyQuaternion(invertQuart)
            .add(new Vector3(groupSizes.x/2,0,0)
                .applyQuaternion(quaternion));
        const upPoint = groupCenter.clone().add(new Vector3(0,groupSizes.y/2,0));
        const downPoint = groupCenter.clone().sub(new Vector3(0,groupSizes.y/2,0));
        // const minY = this.targetGroup[0].position.y;
        // const min
        // const minX = this.targetGroup[0].position.y;

        if (this.upIntersection.object) {
            this.rulerUp.update(new Line3(upPoint, this.upIntersection.point), quaternion);
            // this.rulerUp.model.removeFromParent();
            // this.rulerUp = new TextLine(new Line3(this.target.obb.center, upIntersection.point));
            $.app.scene.add(this.rulerUp.model);
            // this.model.add(this.rulerUp.model);
        } else {
            this.rulerUp.model.removeFromParent();
        }

        if (this.downIntersection.object) {
            this.rulerDown.update(new Line3(downPoint, this.downIntersection.point), quaternion);
            // this.rulerDown.model.removeFromParent();
            // this.rulerDown = new TextLine(new Line3(this.target.rays.down.origin, downIntersection.point));
            $.app.scene.add(this.rulerDown.model);
        } else {
            this.rulerDown.model.removeFromParent();
        }

        if (this.leftIntersection.object) {
            this.rulerLeft.update(new Line3(leftPoint, this.leftIntersection.point), quaternion);
            // this.rulerLeft.model.removeFromParent();
            // this.rulerLeft = new TextLine(new Line3(this.target.rays.left.origin, leftIntersection.point));
            $.app.scene.add(this.rulerLeft.model);
        } else {
            this.rulerLeft.model.removeFromParent();
        }

        if (this.rightIntersection.object) {
            this.rulerRight.update(new Line3(rightPoint, this.rightIntersection.point), quaternion);
            // this.rulerRight.model.removeFromParent();
            // this.rulerRight = new TextLine(new Line3(this.target.rays.right.origin, rightIntersection.point));
            $.app.scene.add(this.rulerRight.model);
        } else {
            this.rulerRight.model.removeFromParent();
        }

    }
    updateRuler() {
        if (!this.target || !$.isRuler) {
            this.rulerUp.model.removeFromParent();
            this.rulerDown.model.removeFromParent();
            this.rulerLeft.model.removeFromParent();
            this.rulerRight.model.removeFromParent();
            return;
        }


        const quaternion = new Quaternion();
        this.target.model.getWorldQuaternion(quaternion);

        if (this.upIntersection.object) {
            this.rulerUp.update(new Line3(this.target.rays.up.origin, this.upIntersection.point), quaternion);
            // this.rulerUp.model.removeFromParent();
            // this.rulerUp = new TextLine(new Line3(this.target.obb.center, upIntersection.point));
            $.app.scene.add(this.rulerUp.model);
            // this.model.add(this.rulerUp.model);
        } else {
            this.rulerUp.model.removeFromParent();
        }

        if (this.downIntersection.object) {
            this.rulerDown.update(new Line3(this.target.rays.down.origin, this.downIntersection.point), quaternion);
            // this.rulerDown.model.removeFromParent();
            // this.rulerDown = new TextLine(new Line3(this.target.rays.down.origin, downIntersection.point));
            $.app.scene.add(this.rulerDown.model);
        } else {
            this.rulerDown.model.removeFromParent();
        }

        if (this.leftIntersection.object) {
            this.rulerLeft.update(new Line3(this.target.rays.left.origin, this.leftIntersection.point), quaternion);
            // this.rulerLeft.model.removeFromParent();
            // this.rulerLeft = new TextLine(new Line3(this.target.rays.left.origin, leftIntersection.point));
            $.app.scene.add(this.rulerLeft.model);
        } else {
            this.rulerLeft.model.removeFromParent();
        }

        if (this.rightIntersection.object) {
            this.rulerRight.update(new Line3(this.target.rays.right.origin, this.rightIntersection.point), quaternion);
            // this.rulerRight.model.removeFromParent();
            // this.rulerRight = new TextLine(new Line3(this.target.rays.right.origin, rightIntersection.point));
            $.app.scene.add(this.rulerRight.model);
        } else {
            this.rulerRight.model.removeFromParent();
        }



    }

    computeObb() {
        if (!this.target && !this.targetGroup.length) return;

        // если выделена группа
        if (this.targetGroup.length) {
            const box = new Box3();
            const minVector = new Vector3();
            const maxVector = new Vector3();
            this.targetGroup.forEach((target)=>{
                const center = target.obb.center.clone();
                const size = new Vector3();
                target.obb.getSize(size);
                (!minVector.x || minVector.x > center.x - size.x/2) && (minVector.x = center.x - size.x/2);
                (!minVector.y || minVector.y > center.y - size.y/2) && (minVector.y = center.y - size.y/2);
                (!minVector.z || minVector.z > center.z - size.z/2) && (minVector.z = center.z - size.z/2);
                (!maxVector.x || maxVector.x < center.x + size.x/2) && (maxVector.x = center.x + size.x/2);
                (!maxVector.y || maxVector.y < center.y + size.y/2) && (maxVector.y = center.y + size.y/2);
                (!maxVector.z || maxVector.z < center.z + size.z/2) && (maxVector.z = center.z + size.z/2);
            });
            box.setFromPoints([ minVector, maxVector ]);
            this.obb.fromBox3(box);
            const center = new Vector3();
            (new Line3(minVector, maxVector)).getCenter(center);
            this.obb.center.copy(center);
            // this.updateGeometry(size.x, size.y, size.z, isGroup);
            return;
        }
        // Если не группа
        if (!this.target) return;
        this.obb.copy(this.target.obb);
    }

    update() {
        this.updateGeometry();
        this.updateIntersections();
        if (this.targetGroup.length) {
            this.updateRulerForGroup();
        }
        else {
            this.updateRuler();
        }
    }

    unselect() {

        // this.model.visible = false;
        this.model.clear();
        this.target = null;
        this.targetGroup = [];
        this.updateRuler();
        useUIOnlyStore().unselect();
        // this.side = null;
    }

    updateDecor() {
        if (!this.target || ![ 'panel', 'shelf' ].includes(this.target.type)) return;

        if ($.isDecoration) {
            $.wardrobe.addDecor();
        } else {
            $.wardrobe.removeDecor();
        }
    }

    setGroupPositionDelta(line: Line3) {
        if (!this.targetGroup.length || !this.side) return;
        if (this.targetGroup.some((rack)=>rack.side !== this.side)) return;
        let oldPosition: Vector3;
        // const delta = line.delta(new v3d.Vector3());
        const delta = new Vector3(
            line.end.x - line.start.x,
            line.end.y - line.start.y,
            - line.end.z + line.start.z,
        );

        const quatern = new v3d.Quaternion();
        // const firstModule = this.targetGroup[0].getFirstModule();
        const firstModule = this.targetGroup[0];
        oldPosition = firstModule.position;
        firstModule.model.getWorldQuaternion(quatern);
        delta.applyQuaternion(quatern);
        this.targetGroup.forEach((module)=>{
            // const oldPosition = module.model.position.clone();
            // module.position = new Vector3()
            //     .copy(module.position)
            //     .add(delta);
            const newPosition = new Vector3()
                .copy(module.position)
                .add(delta);

            module.setPositionWithShelves(newPosition);
        });

        this.updateGeometry();
        this.updateIntersections();
        this.updateRulerForGroup();

        const allGroupShelves = this.targetGroup.map(item=>item.shelves).flat();
        let collision;
        let outOfTheWall;
        allGroupShelves.forEach((shelf)=>{
            if (!collision)
                collision = $.checkShelfCollisionWithWall(shelf, this.side);
            if (!outOfTheWall)
                outOfTheWall = $.room.checkIfNotOutOfWall(shelf);
        });

        if (collision || outOfTheWall) {
            this.targetGroup.forEach((module)=>{
                // const oldPosition = module.model.position.clone();
                // module.position = new Vector3()
                //     .copy(module.position)
                //     .sub(delta);
                const newPosition = new Vector3()
                    .copy(module.position)
                    .sub(delta);

                module.setPositionWithShelves(newPosition);
            });
            this.updateGeometry();
            this.updateIntersections();
            this.updateRulerForGroup();
            return false;
        } else {
            markCollisions();
            return true;
        }
    }

    setPositionDelta(line: Line3, xOnly = false) {
        if (!this.target) return;
        if (this.target.side !== this.side) return;
        let oldPosition: Vector3;
        // const delta = line.delta(new v3d.Vector3());
        const delta = new Vector3(
            line.end.x - line.start.x,
            xOnly?0:(line.end.y - line.start.y),
            - line.end.z + line.start.z,
        );

        const quatern = new v3d.Quaternion();

        this.target.model.getWorldQuaternion(quatern);
        delta.applyQuaternion(quatern);
        oldPosition = this.target.position.clone();
        const newPosition = this.target.position.clone().add(delta);
        if (this.target.type === 'rackModule') {
            const rackModule = this.target as RackModule;
            rackModule.setPositionWithShelves(newPosition);
        } else if ([ 'door', 'window' ].includes(this.target.type)) {
            this.target.position = newPosition;
        }
        else {
            // Можно использовать чтобы поставить объект прямо под курсором
            const position = transformPositionBySide(
                line.end.clone(),
                this.target.side,
                $.room,
            );
            this.target.shelfPosition = new Vector3(...position);//.applyQuaternion(quatern);
        }
        // Можно использовать чтобы поставить объект прямо под курсором
        // const position = transformPositionBySide(
        //     line.end.clone(),
        //     this.side,
        //     $.room,
        // );
        // this.target.position = new Vector3(...position);//.applyQuaternion(quatern);

        this.updateGeometry();
        this.updateIntersections();
        this.updateRuler();
        this.updateDecor();
        let collisions;
        if (!this.target) return;
        if (this.target.type === 'rackModule') {
            this.target.shelves.forEach((shelf)=>{
                if (!collisions)
                    collisions = $.checkShelfCollisionWithWall(shelf, this.side);
            });
            markCollisions();

        }
        else if ([ 'window', 'door' ].includes(this.target.type)) {
            collisions = $.checkCollision(this.target, this.side, []);
            markCollisions();
        }
        else {
            collisions = $.checkShelfCollisionWithWall(this.target, this.side);
            // if (this.target.rackModule && !collisions) {
            //     collisions = $.checkShelfCollisionWithWall(this.target.rackModule, this.side, []);
            // }
        }
        // console.log(collisions);
        if (collisions ||
            this.target.position.y < 0 ||
            this.target.position.y > $.room.height ||
            $.room.checkIfNotOutOfWall(this.target)
        ) {
            if (!this.target) return false;
            if (this.target.type === 'rackModule') {
                const rackModule = this.target as RackModule;
                rackModule.setPositionWithShelves(oldPosition);
            }
            else
                this.target.position = oldPosition;
            this.updateGeometry();
            this.updateIntersections();
            this.updateRuler();
            this.updateDecor();
            return false;
        } else {

            return true;
        }
    }

    updateGeometry() {
        if (!this.side) return;
        this.computeObb();
        this.model.clear();
        if (!this.target && !this.targetGroup.length) return;
        // if (!this.target && !group) return;
        $.app.scene.children
            .filter((item)=>item.name === 'temp')
            .forEach((item)=>item.removeFromParent());


        // const helper = new v3d.Mesh(new v3d.BoxGeometry(1,1,1,1,1,1), new v3d.MeshBasicMaterial({ wireframe: true }));
        const size = new Vector3();
        this.obb.getSize(size);

        const quaternion = new Quaternion();
        this.target?.model.getWorldQuaternion(quaternion) ||
            this.targetGroup[0].model.getWorldQuaternion(quaternion);
        //
        // size.applyQuaternion(quaternion);
        // helper.scale.copy(size);
        // helper.position.copy(this.obb.center);
        // helper.name = 'temp';
        // $.app.scene.add(helper);

        const lines3 = [
            new Line3(new v3d.Vector3(0,0,0), new v3d.Vector3(0,size.y,0)),
            new Line3(new v3d.Vector3(0,size.y,0), new v3d.Vector3(0,size.y,size.z)),
            new Line3(new v3d.Vector3(0,0,0), new v3d.Vector3(0,0,size.z)),
            new Line3(new v3d.Vector3(0,0,size.z), new v3d.Vector3(0,size.y,size.z)),
            new Line3(new v3d.Vector3(0,0,0), new v3d.Vector3(size.x,0,0)),
            new Line3(new v3d.Vector3(size.x,0,0), new v3d.Vector3(size.x,size.y,0)),
            new Line3(new v3d.Vector3(size.x,0,0), new v3d.Vector3(size.x,0,size.z)),
            new Line3(new v3d.Vector3(size.x,0,size.z), new v3d.Vector3(size.x,size.y,size.z)),
            new Line3(new v3d.Vector3(size.x,size.y,0), new v3d.Vector3(size.x,size.y,size.z)),
            new Line3(new v3d.Vector3(0,0,size.z), new v3d.Vector3(size.x,0,size.z)),
            new Line3(new v3d.Vector3(0,size.y,size.z), new v3d.Vector3(size.x,size.y,size.z)),
            new Line3(new v3d.Vector3(0,size.y,0), new v3d.Vector3(size.x,size.y,0)),
        ];

        const addRuler = (line: Line3, shift: Vector3) => {
            const text = ''+ Math.ceil(line.distance()*1000);
            // TODO убрать new, вызывается много раз
            const textY = new TextMesh(text);
            const lineCenter:Vector3 = new Vector3();
            line.getCenter(lineCenter);
            lineCenter.add(shift);
            textY.model.applyQuaternion(quaternion);
            textY.model.position.copy(lineCenter);

            this.model.add(textY.model);
        };
        //
        const yShift = 0.03;
        const xShift = 0.07;

        if (this.side === 'front') {
            addRuler(lines3[10], new Vector3(0,yShift, 0));
            addRuler(lines3[8], new Vector3(xShift,yShift, 0));
            addRuler(lines3[7], new Vector3(xShift,0, 0));
        }

        if (this.side === 'left') {
            addRuler(lines3[8], new Vector3(0,yShift, 0));
            addRuler(lines3[11], new Vector3(0,0, -xShift));
            addRuler(lines3[5], new Vector3(0,0, -xShift));
        }

        if (this.side === 'right') {
            addRuler(lines3[1], new Vector3(0,yShift, 0));
            addRuler(lines3[10], new Vector3(0,0, xShift));
            addRuler(lines3[3], new Vector3(0,0, xShift));
        }

        if (this.side === 'back') {
            addRuler(lines3[11], new Vector3(0,yShift, 0));
            addRuler(lines3[1], new Vector3(-xShift,0, 0));
            addRuler(lines3[0], new Vector3(-xShift,0, 0));
        }

        //
        // const modelLineProto = $.v3dTools.getObjectByName(lineModelName);
        // if (!modelLineProto) {
        //     console.warn('No model find: ', lineModelName);
        //     return;
        // }
        // lines3.forEach((line)=>{
        //     const lineModel = modelLineProto.clone();
        //     const length = line.start.distanceTo(line.end);
        //     const center = new v3d.Vector3;
        //     line.getCenter(center);
        //     lineModel.position.copy(center);
        //     lineModel.scale.setZ(length*40);
        //     lineModel.lookAt(line.end);
        //     this.model.add(lineModel);
        // });
        lines3.forEach((line)=>this.model.add(new LineDotted(line.start, line.end).model));
        const startPosition = new Vector3();
        startPosition.copy(this.obb.center).sub(this.obb.halfSize);
        this.model.position.copy(startPosition);
        // lines.forEach((line)=>this.model.add(line.model));
        // this.visible = false;
    }

}

